import { DEFAULT_DROPDOWN_TOKEN_CLASS_NAME } from './styles';

export type DropdownTokenOption = {
  label: string;
  value: string;
  className?: string;
  presentingRhythmCategory?: string;
};

export function parseDropdownTokenOptions(
  value: string,
): DropdownTokenOption[] {
  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value) as {
      label?: string;
      value?: string;
      className?: string;
      presentingRhythmCategory?: string;
    }[];

    return parsed
      .map((option) => ({
        label: option.label ?? option.value ?? '',
        value: option.value ?? '',
        className: option.className,
        presentingRhythmCategory: option.presentingRhythmCategory,
      }))
      .filter((option) => !!option.value);
  } catch {
    return [];
  }
}

export type TokenOptionGroup = {
  category: string;
  options: DropdownTokenOption[];
};

export function parseMultiSelectTokenValues(value: string): string[] {
  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value) as unknown;
    if (Array.isArray(parsed)) {
      return parsed.map((item) => String(item)).filter((item) => item !== '');
    }
    return value.trim() ? [value.trim()] : [];
  } catch {
    return value.trim() ? [value.trim()] : [];
  }
}

export function groupTokenOptionsByCategory(
  options: DropdownTokenOption[],
): TokenOptionGroup[] {
  const groups = new Map<string, DropdownTokenOption[]>();
  for (const option of options) {
    const category = option.presentingRhythmCategory?.trim() || 'Uncategorized';
    const group = groups.get(category) ?? [];
    group.push(option);
    groups.set(category, group);
  }

  return Array.from(groups.entries()).map(([category, groupedOptions]) => ({
    category,
    options: groupedOptions,
  }));
}

interface GetReadOnlyDropdownTokenPresentationArgs {
  value: string;
  optionsValue: string;
  emptyFallback?: string;
}

export function getReadOnlyDropdownTokenPresentation({
  value,
  optionsValue,
  emptyFallback = '-',
}: GetReadOnlyDropdownTokenPresentationArgs): {
  displayValue: string;
  optionClassName: string;
} {
  const options = parseDropdownTokenOptions(optionsValue);
  const selectedOption = options.find((option) => option.value === value);

  return {
    displayValue:
      selectedOption?.label ?? (value?.trim() ? value : emptyFallback),
    optionClassName:
      selectedOption?.className || DEFAULT_DROPDOWN_TOKEN_CLASS_NAME,
  };
}
